import React from 'react';
import {
  Box, Typography, Card, CardMedia, Divider, Chip, 
  LinearProgress, Alert
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const BookPreviewCard = ({ 
  book, 
  bulkMode = false, 
  currentIsbn = '', 
  currentIndex = 0, 
  totalCount = 0,
  progress = 0
}) => {
  if (!book) return null;
  
  return (
    <Box>
      {bulkMode && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2">
              Processing ISBN {currentIndex + 1} of {totalCount}: {currentIsbn}
            </Typography>
            <Chip 
              label={`${totalCount - currentIndex} remaining`} 
              size="small" 
              color="primary" 
              variant="outlined" 
            />
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ mt: 1, height: 4, borderRadius: 2 }}
          />
        </Box>
      )}
      
      <Alert 
        severity="success" 
        icon={<CheckCircleOutlineIcon fontSize="inherit" />}
        sx={{ mb: 3 }}
      >
        Book was successfully created and is ready to be published.
      </Alert>
      
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        <Card sx={{ width: 150, height: 225, flexShrink: 0 }}>
          <CardMedia
            component="img"
            image={book.cover || 'https://placehold.co/150x225?text=No+Cover'}
            alt={book.title}
            sx={{ height: '100%', objectFit: 'cover' }}
          />
        </Card>
        
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" gutterBottom>
            {book.title || 'No Title'}
          </Typography>
          {book.subtitle && (
            <Typography variant="subtitle2" gutterBottom>
              {book.subtitle}
            </Typography>
          )}
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            {book.author || 'Unknown Author'}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip 
              label={book.status || 'draft'} 
              color={book.status === 'published' ? 'success' : 'default'}
              size="small"
            />
            {book.isbn && (
              <Chip label={`ISBN: ${book.isbn}`} size="small" variant="outlined" />
            )}
            {book.publishedYear && (
              <Chip label={`Year: ${book.publishedYear}`} size="small" variant="outlined" />
            )}
            {book.pageCount && (
              <Chip label={`${book.pageCount} pages`} size="small" variant="outlined" />
            )}
            {book.publisher && (
              <Chip label={`${book.publisher}`} size="small" variant="outlined" />
            )}
          </Box>
        </Box>
      </Box>
      
      <Divider sx={{ my: 2 }} />
      
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Description
      </Typography>
      <Typography variant="body2" paragraph>
        {book.description || 'No description available.'}
      </Typography>
      
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Key Ideas
      </Typography>
      <Typography variant="body2" paragraph>
        {book.keyIdeas || 'No key ideas available.'}
      </Typography>
      
      {book.categories && Array.isArray(book.categories) && book.categories.length > 0 && (
        <>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Categories
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {book.categories.map((cat, idx) => (
              <Chip 
                key={idx} 
                label={typeof cat === 'object' ? cat.name : cat} 
                size="small" 
              />
            ))}
          </Box>
        </>
      )}
      
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" color="text.secondary">Book Details</Typography>
      <Box sx={{ mt: 1 }}>
        <Typography variant="body2">
          <strong>Book ID:</strong> {book.id}
        </Typography>
        {book.createdAt && (
          <Typography variant="body2">
            <strong>Created:</strong> {new Date(book.createdAt).toLocaleString()}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default BookPreviewCard;
